import { lastValueFrom } from "rxjs";
import { createEffect, createResource, createSignal, For, Show } from "solid-js";

import { TracksEditor } from "./TracksEditor";
import type { UtdoDisk, UtdoLyrics, UtdoTrack } from "../types";
import { action } from "../util/action";
import { useApi } from "../util/api";
import { panic } from "../util/panic";
import { success } from "../util/success";

export function Tracks() {
    const api = useApi();
    const [ tracks, { refetch } ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectTracks());
            } catch (err: any) {
                panic(err);
                return [] as UtdoTrack[];
            }
        },
        { initialValue: [] as UtdoTrack[] },
    );
    const [ songs ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectLyrics());
            } catch (err: any) {
                panic(err);
                return [] as UtdoLyrics[];
            }
        },
        { initialValue: [] as UtdoLyrics[] },
    );
    const [ disks ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectDisk());
            } catch (err: any) {
                panic(err);
                return [] as UtdoDisk[];
            }
        },
        { initialValue: [] as UtdoDisk[] },
    );
    const [ selectedDisk, setSelectedDisk ] = createSignal<number|undefined>(undefined);
    const [ selectedTrack, setSelectedTrack ] = createSignal<number|undefined>(undefined);

    const selectDisk = (ev: Event) => {
        if (!(ev.target instanceof HTMLSelectElement)) {
            return;
        }
        let id: number | undefined = Number.parseInt(ev.target.value, 10);
        if (Number.isNaN(id)) {
            id = undefined;
        }
        setSelectedDisk(id);
    };

    const selectedTracks = () => {
        const diskId = selectedDisk();
        if (!diskId) {
            return [] as UtdoTrack[];
        }
        return tracks().reduce(
            (acc, track) => {
                if (track.disk !== diskId) {
                    return acc;
                }
                if (!track.title) {
                    const song = songs().find((song) => song.id == track.lyrics);
                    acc.push({
                        ...track,
                        title: song?.title,
                    });
                    return acc;
                }
                acc.push(track);
                return acc;
            },
            [] as UtdoTrack[]
        );
    };

    let dialogRef!: HTMLDialogElement;

    const currentTrack = (): UtdoTrack => {
        const id = selectedTrack();
        return tracks().find((track) => track.id === id) ?? {
            id: 0,
            disk: selectedDisk() ?? 0,
            track: selectedTracks()
                .reduce((acc, val) => val.track > acc ? val.track : acc , 0) + 1,
            lyrics: 0,
            duration: "00:00:01",
        };
    };

    createEffect(() => {
        if (selectedTrack() === undefined) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    });

    const deleteTrack = action(
        async (track: UtdoTrack) => {
            if (!confirm(`Vous allez supprimer la piste "${track.title}".\nContinuer ?`)) {
                return;
            }
            await lastValueFrom(api.deleteTrack(track.id!));
        },
        refetch,
    );

    const editTrack = (id?: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setSelectedTrack(id);
    };

    const save = async (track: UtdoTrack) => {
        try {
            if ((track.id ?? 0) > 0) {
                if (await lastValueFrom(api.updateTrack(track))) {
                    success("Piste enregistrée avec succès.");
                }
            } else {
                if (await lastValueFrom(api.insertTrack(track))) {
                    success("Piste enregistée avec succès.");
                }
            }
            setSelectedTrack(undefined);
            refetch();
        } catch (err: any) {
            panic(err);
        }
    };

    return <section id="admin-tracks">
        <h2>Pistes</h2>
        <div>
            <div class="actions">
                <select onChange={selectDisk}>
                    <option selected>Aucun</option>
                    <For each={disks()}>
                        {(disk) => <option value={disk.id}>{disk.title}</option>}
                    </For>
                </select>
            </div>
            {((tracks: UtdoTrack[]) => <>
                <ul>
                    <For each={tracks}>
                        {(track) => <li>
                            <span>{track.track}</span>
                            <a href="#" onClick={editTrack(track.id!)}>{track.title}</a>
                            <span>{track.duration}</span>
                            <div class="actions">
                                <a href="#" class="icon"
                                    onClick={deleteTrack(track)}>delete</a>
                            </div>
                        </li>}
                    </For>
                </ul>
            </>)(selectedTracks())}
            <Show when={(selectedDisk() ?? 0) > 0}>
                <div class="actions">
                    <button onClick={editTrack(0)}>Ajouter une piste</button>
                </div>
            </Show>
            {/*  */}
        </div>
        <dialog ref={dialogRef}>
            <TracksEditor
                track={currentTrack()}
                lyrics={songs()}
                validate={save}
                cancel={() => setSelectedTrack(undefined)}
                />
        </dialog>
    </section>;
}